import type { AudienceFilters } from '../types';
import { commerceAttributeLabel } from './commerceAttributes';
import { DEFAULT_PURCHASE_LOOKBACK_DAYS, type FilterGroupId } from './filters';
import { stateName } from './usStates';

/**
 * One chip in a filter summary.
 *
 * `clearPatch` removes just this chip's filter, and is null for a chip that cannot be cleared.
 */
export type FilterChip = {
  id: string;
  group: FilterGroupId;
  label: string;
  tone: 'include' | 'exclude';
  clearPatch: Partial<AudienceFilters> | null;
};

export type DescribeFiltersOptions = {
  brandLabel?: (brandId: string) => string;
};

/** The purchase-window chip label for a window of `days`. */
export const lookbackLabel = (days: number): string => `Purchased in the last ${days} days`;

/** The default purchase window, worded as the Active chip words it, for a definition with no rules. */
export const DEFAULT_WINDOW_LABEL = lookbackLabel(DEFAULT_PURCHASE_LOOKBACK_DAYS);

/**
 * Turns a filter set into grouped, individually clearable chips.
 *
 * - Ranges become one chip that clears both bounds.
 * - Each array item becomes its own chip.
 * - The purchase window always applies, so it always has a chip, showing `DEFAULT_PURCHASE_LOOKBACK_DAYS` when `purchaseLookbackDays` is null.
 * - The purchase-window chip has no clear patch.
 */
export const describeFilters = (
  filters: AudienceFilters,
  { brandLabel = id => id }: DescribeFiltersOptions = {}
): FilterChip[] => {
  const chips: FilterChip[] = [];
  const push = (chip: Omit<FilterChip, 'tone'> & { tone?: FilterChip['tone'] }): void => {
    chips.push({ tone: 'include', ...chip });
  };

  if (filters.gender)
    push({
      id: 'gender',
      group: 'demographics',
      label: filters.gender === 'female' ? 'Female' : 'Male',
      clearPatch: { gender: null }
    });
  const age = rangeLabel('Age', filters.ageMin, filters.ageMax);
  if (age)
    push({
      id: 'age',
      group: 'demographics',
      label: age,
      clearPatch: { ageMin: null, ageMax: null }
    });
  const income = rangeLabel('Income', filters.incomeMin, filters.incomeMax, formatIncome);
  if (income)
    push({
      id: 'income',
      group: 'demographics',
      label: `${income} (zip)`,
      clearPatch: { incomeMin: null, incomeMax: null }
    });

  for (const code of filters.includedStates) {
    push({
      id: `in-state-${code}`,
      group: 'geography',
      label: stateName(code),
      clearPatch: { includedStates: removeItem(filters.includedStates, code) }
    });
  }
  for (const code of filters.excludedStates) {
    push({
      id: `ex-state-${code}`,
      group: 'geography',
      label: `Not ${stateName(code)}`,
      tone: 'exclude',
      clearPatch: { excludedStates: removeItem(filters.excludedStates, code) }
    });
  }

  for (const key of filters.commerceAttributes) {
    push({
      id: `attr-${key}`,
      group: 'interests',
      label: commerceAttributeLabel(key),
      clearPatch: { commerceAttributes: removeItem(filters.commerceAttributes, key) }
    });
  }

  for (const id of filters.includedBrandIds) {
    push({
      id: `in-brand-${id}`,
      group: 'brands',
      label: `Bought ${brandLabel(id)}`,
      clearPatch: { includedBrandIds: removeItem(filters.includedBrandIds, id) }
    });
  }
  for (const id of filters.excludedBrandIds) {
    push({
      id: `ex-brand-${id}`,
      group: 'brands',
      label: `Not ${brandLabel(id)}`,
      tone: 'exclude',
      clearPatch: { excludedBrandIds: removeItem(filters.excludedBrandIds, id) }
    });
  }

  push({
    id: 'lookback',
    group: 'behaviors',
    label: lookbackLabel(filters.purchaseLookbackDays ?? DEFAULT_PURCHASE_LOOKBACK_DAYS),
    clearPatch: null
  });
  const orders = rangeLabel('Orders', filters.orderCountMin, filters.orderCountMax);
  if (orders)
    push({
      id: 'orders',
      group: 'behaviors',
      label: orders,
      clearPatch: { orderCountMin: null, orderCountMax: null }
    });
  if (filters.hasDiscountPurchase != null)
    push({
      id: 'discount',
      group: 'behaviors',
      label: filters.hasDiscountPurchase ? 'Used a discount' : 'Never used a discount',
      tone: filters.hasDiscountPurchase ? 'include' : 'exclude',
      clearPatch: { hasDiscountPurchase: null }
    });

  if (filters.uniqueIndividualLimit != null)
    push({
      id: 'size',
      group: 'size',
      label: `Top ${formatCount(filters.uniqueIndividualLimit)} by LTV`,
      clearPatch: { uniqueIndividualLimit: null, rankColumn: null }
    });

  return chips;
};

/* 80000 -> "$80k"; 125000 -> "$125k"; 1500000 -> "$1.5M" */
export const formatIncome = (value: number): string => {
  if (value >= 1_000_000) return `$${Number((value / 1_000_000).toFixed(1))}M`;
  if (value >= 1000) return `$${Number((value / 1000).toFixed(1))}k`;
  return `$${value}`;
};

const rangeLabel = (
  noun: string,
  min: number | null,
  max: number | null,
  format: (value: number) => string = String
): string | null => {
  if (min != null && max != null) return `${noun} ${format(min)}–${format(max)}`;
  if (min != null) return `${noun} ${format(min)}+`;
  if (max != null) return `${noun} up to ${format(max)}`;
  return null;
};

const removeItem = (items: string[], item: string) => items.filter(value => value !== item);

const formatCount = (value: number) => value.toLocaleString('en-US');
